import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HomePage } from "./pages/HomePage";
import { ProvidersPage } from "./pages/ProvidersPage";

import { ClinicianProfilePage } from "./pages/ClinicianProfilePage";
import { ClinicianProfileEditPage } from "./pages/ClinicianProfileEditPage";
import { GroupDashboardPage } from "./pages/GroupDashboardPage";
import BlogListPage  from "./pages/BlogListPage";
import { BlogDashboardPage } from "./pages/BlogDashboardPage";
import { BlogPostPage } from "./pages/BlogPostPage";
import { LoginPage } from "./pages/LoginPage";
import  RegistrationPage from "./pages/RegistrationPage/RegistrationPage";
import { EmailConfirmationPage } from "./pages/EmailConfirmationPage";
import { EditorLoginPage } from "./pages/EditorLoginPage";
import { EditorDashboardPage } from "./pages/EditorDashboardPage";
// import { useAuthStore } from "./store/authStore";
import AdsPage from "./pages/AdsPage";
import { MatchmakingPage } from "./pages/MatchmakingPage";
import   AnalyticsPage  from "./pages/AnalyticsPage";
import { IntegrationsPage } from "./pages/IntegrationsPage";
import  ReferralsPage  from "./pages/ReferralsPage";
import { GroupSettingsPage } from "./pages/GroupSettingsPage";
import { ClinicianManagementPage } from "./pages/ClinicianManagementPage";

import { VerifiedBadgeEmbedPage } from "./pages/VerifiedBadgeEmbedPage";
import { InsuranceVerificationPage } from "./pages/InsuranceVerificationPage";
import { OnboardingPage } from "./pages/OnboardingPage";
import { ChallengePage } from "./pages/ChallengePage/ChallengePage";
import MainLayout from "./layouts/MainLayout";
import RequireAuth from "./components/auth/RequireAuth";
import RequireNoAuth from "./components/auth/RequireNoAuth";
import AuthLayout from "./layouts/AuthLayout";
import { AdminBlogPage } from "./pages/AdminBlog";
import BlogEditPage from './pages/BlogEditPage';
import ClinicianPublicPage from './pages/ClinicianPublicPage';
import { CliniciansPage } from "./pages/CliniciansPage";
import InvitationManagement from './pages/InvitationManagement';
import WorkforceBillingPage from './pages/WorkforceBillingPage';
import ClinicianDetailPage from "./pages/ClinicianDetailsPage";
// Toatss message
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css"
import VerificationPage  from "./pages/VerificationPage";
import ClinicianEditPage from "./pages/ClinicianEditPage";
import BlogCreatePage from "./pages/BlogCreatePage";  

import TeamPage from './pages/TeamPage';
import IntakeCoordinatorPage from "./pages/RegistrationPage/IntakeCoordinatorPage";
import ConfirmEmailPage from "./pages/ConfirmEmailPage";
import SimplePracticePage from "./pages/Integration/SimplePracticePage";
import SimplePracticeIntegrationForm from "./pages/Integration/SimplePracticeIntegrationForm";
import SimplePracticeIntegrationDetails from "./pages/Integration/SimplePracticeIntegrationDetails";
import ForgetPasswordPage from "./pages/ChallengePage/ForgetPasswordPage";
import ContactSupportPage from "./pages/ContactSupportPage";
import AdsDetailsPage from "./pages/Advertise/AdsDetailsPage";
import ScrollToTop from "./components/ScrollToTop";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsOfServicePage from "./pages/TermsOfServicePage";
import AccessibilityPage from "./pages/AccessibilityPage";
import HIPAANoticePage from "./pages/HIPAANoticePage";
import CookiePolicyPage from "./pages/CookiePolicyPage";
import AboutUsPage from "./pages/AboutUsPage";
import ContactPage from "./pages/ContactPage";
import AddClinicianPage from "./pages/AddClinicianPage";
import PricingPage from "./pages/PricingPage";
import SimplePracticeInvitationPage from "./pages/SimplePracticeInvitationPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import RewardfulTracker from "./components/RewardfulTracker";

const queryClient = new QueryClient();

const App: React.FC = () => {

  return (  
    <QueryClientProvider client={queryClient}>
      <ToastContainer position="top-right" autoClose={3000} />
      <Router>
        <ScrollToTop />
        <RewardfulTracker />
        <Routes>
          <Route element={<MainLayout />}>
            <Route path="/" element={<HomePage />} />
            {/* <Route path="/pricing" element={<PricingPage />} /> */}
            {/* <Route path="/invitation" element={<SimplePracticeInvitationPage />} /> */}

            <Route path="/find-therapist" element={<ProvidersPage />} />
            <Route path="/clinician/:clinicianId" element={<ClinicianPublicPage />} />
            <Route path="/clinician/invitation" element={<VerificationPage  />} />

            <Route path="/blog" element={<BlogListPage />} />
            <Route path="/blog/manage" element={<BlogDashboardPage />} />
            <Route path="/admin/blog" element={<AdminBlogPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/onboarding" element={ <OnboardingPage />} />
            <Route path="/forgot-password" element={ <ForgetPasswordPage />} />
            <Route path="/contact-support" element={ <ContactSupportPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/accessibility" element={<AccessibilityPage />} />
            <Route path="/hipaa-notice" element={<HIPAANoticePage />} />
            <Route path="/cookies" element={<CookiePolicyPage />} />
            <Route path="/about" element={<AboutUsPage />} />
            <Route path="/contact" element={<ContactPage />} />
          </Route>

          {/* Editor routes - no header */}
          <Route path="/editor/login" element={<EditorLoginPage />} />
          <Route path="/editor/dashboard" element={<EditorDashboardPage />} />

          {/* Auth routes - no header */}
          <Route path="/verify-email" element={<EmailConfirmationPage />} />
          <Route path="/challenge" element={<ChallengePage />} />

          {/* Embed routes - no header */}
          <Route
            path="/embed/verified-badge/:clinicianId"
            element={<VerifiedBadgeEmbedPage />}/>

          {/* Main routes with header */}
          <Route element={<RequireAuth><MainLayout /></RequireAuth>}>
            <Route path="/providers/:id" element={<ClinicianProfilePage />} />
            <Route path="/providers/:id/edit" element={<ClinicianProfileEditPage />}
           />

            <Route path="practice">
              {/* <Route index element={<GroupsPage />} /> */}
              <Route path=""             element={<GroupDashboardPage />} />
              <Route path="blog/create" element={<BlogCreatePage />} />
              {/* <Route path="" element={<GroupDashboardPage />} /> */}
              <Route path="clinicians"   element={<CliniciansPage />}/>
              
              <Route path="clinicians/edit/:clinicianId" element={<ClinicianManagementPage />}/>
              <Route path="analytics"          element={<AnalyticsPage />} />
              <Route path="add-clinician"       element={<AddClinicianPage />} />
              <Route path="matchmaking"        element={<MatchmakingPage />} />
              <Route path="advertise"          element={<AdsPage />} />
              <Route path="advertise/details"  element={<AdsDetailsPage practiceId="current-practice" />} />
              <Route path="blog"               element={<BlogDashboardPage />} />
              <Route path="clinician/details/:id"    element={<ClinicianDetailPage />} />
              <Route path="clinician/edit/:id" element={<ClinicianEditPage />} />
              <Route path="blog/edit/:blogId"  element={<BlogEditPage />} />
              <Route path="integrations"       element={<IntegrationsPage />} />
              <Route path="integration/simplepractice" element={<SimplePracticePage />} />
              <Route path="referrals"          element={<ReferralsPage />} />
              <Route path="workforce"        element={<InvitationManagement />} />
              <Route path="workforce/billing" element={<WorkforceBillingPage />} />
              <Route path="invitation-management/payment/success" element={<PaymentSuccessPage />} />
              <Route path="settings"           element={<GroupSettingsPage />} />
              <Route path="blog/edit/:blogId"  element={<BlogEditPage />} />
              <Route path="invite/intakecoordinator" element={<IntakeCoordinatorPage />} />
              <Route path="integration/simplepractice/form" element={<SimplePracticeIntegrationForm />} />
              <Route path="integration/simplepractice/details" element={<SimplePracticeIntegrationDetails />} />
              <Route path="teams" element={<TeamPage />} />

              <Route
                path="insurance"
                element={<InsuranceVerificationPage />}
              />
            </Route>
          </Route>

          {/* Auth routes */}
          <Route element={ <RequireNoAuth><AuthLayout /></RequireNoAuth> }>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegistrationPage />} />
            <Route path="/confirm-email" element={<ConfirmEmailPage />} />
          </Route>

          {/* Wildcard route for 404 page not found */}
          <Route element={<MainLayout />}>
            <Route path="*" element={
              <div className="flex flex-col items-center justify-center min-h-screen">
                <h1 className="text-4xl font-bold mb-4">404</h1>
                <p className="text-xl mb-8">Page not found</p>
                <a href="/" className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600">
                  Return to Home
                </a>
              </div>
            } />
          </Route>
        </Routes>
      </Router>
    </QueryClientProvider>
  );
};

export default App;
